import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import {
  getNotificationUnreadCount,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
} from '@/Api';
import { isApiError } from '@/services/httpClient';

const POLL_MS = 60_000;
/** After gateway failures, wait at least this long before the next poll. */
const GATEWAY_BACKOFF_MIN_MS = 2 * 60_000;
const MAX_BACKOFF_MS = 10 * 60_000;
const LEADER_LOCK = 'robotop-notification-poll';
const SYNC_CHANNEL = 'robotop-notifications';

type SyncMessage =
  | { type: 'unread'; count: number }
  | { type: 'items'; items: AppNotification[]; unreadCount: number };

function isDocumentVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = window.setTimeout(resolve, ms);
    const onAbort = () => {
      window.clearTimeout(id);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function postSync(msg: SyncMessage) {
  try {
    const ch = new BroadcastChannel(SYNC_CHANNEL);
    ch.postMessage(msg);
    ch.close();
  } catch {
    // BroadcastChannel unsupported — ignore
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

function isGatewayError(err: unknown): boolean {
  return isApiError(err) && [502, 503, 504].includes(err.response.status);
}

/**
 * Notification badge + dropdown data.
 * - Background poll: unread count only (not the full list every 60s).
 * - Paused while the tab is hidden; resumes with a fresh count when visible.
 * - Only one visible tab polls (Web Locks); others sync via BroadcastChannel.
 * - Gateway failures (502) use long backoff and do not restart the poll loop every second.
 */
export function useNotificationCenter() {
  const { t } = useTranslation();
  const session = useSessionContext();
  const isLoggedIn = !session.loading && session.doesSessionExist;
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const failStreakRef = useRef(0);
  const lastErrorLogAtRef = useRef(0);

  const setUnreadAndBroadcast = useCallback((count: number) => {
    setUnreadCount(count);
    postSync({ type: 'unread', count });
  }, []);

  const logPollError = useCallback((label: string, err: unknown) => {
    // Rate-limit console noise when Core/nginx is down (avoids flooding DevTools).
    const now = Date.now();
    if (now - lastErrorLogAtRef.current < 60_000) return;
    lastErrorLogAtRef.current = now;
    console.error(label, err);
  }, []);

  const nextDelayMs = useCallback(() => {
    const streak = failStreakRef.current;
    if (streak === 0) return POLL_MS;
    // streak 1 → 2m, 2 → 4m, … capped
    const exp = Math.min(GATEWAY_BACKOFF_MIN_MS * 2 ** (streak - 1), MAX_BACKOFF_MS);
    return exp;
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!isLoggedIn) {
      setItems([]);
      setUnreadCount(0);
      failStreakRef.current = 0;
      return;
    }
    try {
      const count = await getNotificationUnreadCount();
      setUnreadAndBroadcast(count);
      failStreakRef.current = 0;
    } catch (err) {
      failStreakRef.current += 1;
      logPollError('Failed to load notification unread count:', err);
    }
  }, [isLoggedIn, setUnreadAndBroadcast, logPollError]);

  const openAndRefresh = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    try {
      const list = await getNotifications(40);
      failStreakRef.current = 0;
      const hasUnread = list.some((n) => !n.readAt);
      if (hasUnread) {
        await markAllNotificationsRead();
        const now = new Date().toISOString();
        const marked = list.map((n) => ({ ...n, readAt: n.readAt ?? now }));
        setItems(marked);
        setUnreadAndBroadcast(0);
        postSync({ type: 'items', items: marked, unreadCount: 0 });
      } else {
        setItems(list);
        setUnreadAndBroadcast(0);
        postSync({ type: 'items', items: list, unreadCount: 0 });
      }
    } catch (err) {
      failStreakRef.current += 1;
      logPollError('Failed to open notifications:', err);
      if (isGatewayError(err)) {
        notifications.show({
          title: t('notificationCenter.errorTitle'),
          message: t('notificationCenter.loadError'),
          color: 'red',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, setUnreadAndBroadcast, logPollError, t]);

  // Cross-tab sync: followers update badge without polling
  useEffect(() => {
    if (!isLoggedIn) return;
    let ch: BroadcastChannel;
    try {
      ch = new BroadcastChannel(SYNC_CHANNEL);
    } catch {
      return;
    }
    ch.onmessage = (event: MessageEvent<SyncMessage>) => {
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'unread') {
        setUnreadCount(msg.count);
        return;
      }
      if (msg.type === 'items') {
        setItems(msg.items);
        setUnreadCount(msg.unreadCount);
      }
    };
    return () => ch.close();
  }, [isLoggedIn]);

  // Leader tab polls unread count while visible; hidden tabs do nothing
  useEffect(() => {
    if (!isLoggedIn) {
      setItems([]);
      setUnreadCount(0);
      failStreakRef.current = 0;
      return;
    }

    const effectAbort = new AbortController();
    let sessionAbort: AbortController | null = null;

    const stopSession = () => {
      sessionAbort?.abort();
      sessionAbort = null;
    };

    const runVisibleSession = async (signal: AbortSignal) => {
      const locks = navigator.locks;
      try {
        if (locks?.request) {
          await locks.request(LEADER_LOCK, { signal }, async () => {
            while (!signal.aborted && isDocumentVisible()) {
              await refreshUnread();
              await sleep(nextDelayMs(), signal);
            }
          });
        } else {
          while (!signal.aborted && isDocumentVisible()) {
            await refreshUnread();
            await sleep(nextDelayMs(), signal);
          }
        }
      } catch (err) {
        if (!isAbortError(err)) {
          logPollError('Notification poll leader loop failed:', err);
        }
      }
    };

    const startVisibleSession = () => {
      stopSession();
      if (effectAbort.signal.aborted || !isDocumentVisible()) return;

      const localAbort = new AbortController();
      sessionAbort = localAbort;
      const onEffectAbort = () => localAbort.abort();
      effectAbort.signal.addEventListener('abort', onEffectAbort, { once: true });

      void runVisibleSession(localAbort.signal).finally(() => {
        effectAbort.signal.removeEventListener('abort', onEffectAbort);
        // Do not auto-restart here — a 1s reclaim loop was spamming 502s when
        // Core/nginx failed. Visibility change / remount starts a new session.
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startVisibleSession();
      } else {
        stopSession();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    if (isDocumentVisible()) startVisibleSession();

    return () => {
      effectAbort.abort();
      stopSession();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isLoggedIn, refreshUnread, nextDelayMs, logPollError]);

  const markRead = useCallback(async (id: string) => {
    try {
      await markNotificationRead(id);
      setItems((prev) =>
        prev.map((n) =>
          n._id === id ? { ...n, readAt: n.readAt ?? new Date().toISOString() } : n
        )
      );
      setUnreadCount((c) => {
        const next = Math.max(0, c - 1);
        postSync({ type: 'unread', count: next });
        return next;
      });
    } catch (err) {
      console.error('Failed to mark notification read:', err);
      notifications.show({
        title: t('notificationCenter.errorTitle'),
        message: t('notificationCenter.markReadError'),
        color: 'red',
      });
    }
  }, [t]);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      const now = new Date().toISOString();
      setItems((prev) => {
        const next = prev.map((n) => ({ ...n, readAt: n.readAt ?? now }));
        postSync({ type: 'items', items: next, unreadCount: 0 });
        return next;
      });
      setUnreadAndBroadcast(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      notifications.show({
        title: t('notificationCenter.errorTitle'),
        message: t('notificationCenter.markAllReadError'),
        color: 'red',
      });
    }
  }, [setUnreadAndBroadcast, t]);

  return {
    isLoggedIn,
    items,
    unreadCount,
    loading,
    refresh: refreshUnread,
    openAndRefresh,
    markRead,
    markAllRead,
  };
}
